import { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';

import { ProviderSelector } from './components/ProviderSelector';
import { TrendSearchPage } from './components/TrendSearchPage';
import { QueryInput } from './components/QueryInput';
import { MultiProviderGrid } from './components/MultiProviderGrid';
import { ProviderResultColumn } from './components/ProviderResultColumn';
import { SimplifiedSearchColumn } from './components/SimplifiedSearchColumn';
import { InitializationLoader } from './components/InitializationLoader';
import { ExecutionHistoryPage } from './components/ExecutionHistoryPage';
import { EvaluationListView } from './components/EvaluationListView';
import { CreateEvaluationTestPage } from './components/CreateEvaluationTestPage';
import { EditEvaluationTestPage } from './components/EditEvaluationTestPage';
import { EvaluationExecutionInterface } from './components/EvaluationExecutionInterface';
import { EvaluationAnalyticsView } from './components/EvaluationAnalyticsView';
import { AIRetestPage } from './components/AIRetestPage';
import { BatchRunPage } from './components/BatchRunPage';
import { AIComparisonView } from './components/AIComparisonView';
import { ScoringValidationListPage } from './components/ScoringValidationListPage';
import { CreateScoringValidationPage } from './components/CreateScoringValidationPage';
import { ScoringValidationDashboard } from './components/ScoringValidationDashboard';
import { SystemPromptsPage } from './components/SystemPromptsPage';
import { ModelValidationPage } from './components/ModelValidationPage';
import { HallucinationTestsPage } from './components/HallucinationTestsPage';
import { PrecomputedTrendsPage } from './components/PrecomputedTrendsPage';
import { NotFoundPage } from './components/NotFoundPage';
import { SignalRProvider } from './contexts/SignalRContext';
import { useSignalR } from './contexts/useSignalR';
import { HealthStatusProvider } from './contexts/HealthStatusContext';
import { useHealthStatus } from './contexts/useHealthStatus';
import { useMultiProviderQuery } from './hooks/useMultiProviderQuery';
import { usePromptCache } from './hooks/usePromptCache';
import { useViewMode } from './hooks/useViewMode';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { useDebugMode } from './hooks/useDebugMode';
import { ApiService } from './services/apiService';
import { AgentType, AgentModelOverride } from './types/models';
import { ScoringResults } from './types';
import { Scale, Award } from 'lucide-react';
import './styles/debug.css';

const CUSTOM_CONFIGS_KEY = 'custom-model-configs';
const SELECTED_CONFIGS_KEY = 'selected-custom-config-ids';

function MainPage() {
  const {
    selectedProviders,
    setSelectedProviders,
    prompt,
    setPrompt,
    submittedPrompt,
    isLoading,
    hasSubmitted,
    executionFlows,
    providerExecutions,
    executionEvents,
    submitQuery,
    refreshProvider,
    setProviderMaxOpinions: setProviderMaxOpinionsInHook,
    setProviderMaxContraryOpinions: setProviderMaxContraryOpinionsInHook,
    setProviderMaxPassages: setProviderMaxPassagesInHook,
    setProviderEmbeddingIndexes: setProviderEmbeddingIndexesInHook,
    setProviderHitl: setProviderHitlInHook,
    setProviderFolioThreshold: setProviderFolioThresholdInHook,
    setProviderRelevanceCheck: setProviderRelevanceCheckInHook,
    setProviderReducer: setProviderReducerInHook,
    setProviderMinSearchScore: setProviderMinSearchScoreInHook,
    usedSessionContext,
    sessionCanContinue,
    sessionLimitReached,
    sessionHistory,
    startNewSession
  } = useMultiProviderQuery();

  const {
    cachedPrompts,
    addToCache,
    removeFromCache
  } = usePromptCache();
  
  const { connectionState } = useSignalR();
  const { viewMode } = useViewMode();
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [selectedCustomConfigs, setSelectedCustomConfigs] = useState<Array<{ id: string; name: string; config: Record<AgentType, AgentModelOverride | undefined> }>>([]);
  const selectAllRef = useRef<(() => void) | null>(null);
  const [scoringResults, setScoringResults] = useState<ScoringResults | null>(null);
  const [isScoring, setIsScoring] = useState(false);
  const apiService = ApiService.getInstance();
  const signalRContext = useSignalR();

  // Determine if simplified view should be used (user mode = simplified)
  const isSimplifiedView = viewMode === 'user';

  // Load selected custom configs from localStorage on mount,
  // then validate systemPromptHash values against active prompts from the API.
  // This ensures stale hashes (from disabled/deleted prompts) are cleared even if
  // the user never opens ProviderSelector.
  useEffect(() => {
    const loadAndValidateConfigs = async () => {
      try {
        const storedConfigIds = localStorage.getItem(SELECTED_CONFIGS_KEY);
        if (!storedConfigIds) return;

        const ids = JSON.parse(storedConfigIds);
        const allConfigs = JSON.parse(localStorage.getItem(CUSTOM_CONFIGS_KEY) || '[]');
        const selectedConfigs = allConfigs.filter((c: any) => ids.includes(c.id));

        if (selectedConfigs.length === 0) return;

        // Fetch active prompts to validate stored hashes
        let activeHashes: Set<string> | null = null;
        try {
          const activePrompts = await apiService.getSystemPrompts(false);
          activeHashes = new Set(activePrompts.map(p => p.hash));
        } catch {
          // If fetch fails, still load configs — backend will reject stale hashes
        }

        // Prune stale systemPromptHash values from configs
        if (activeHashes) {
          let configsModified = false;
          for (const config of allConfigs) {
            for (const agentKey of Object.keys(config.config || {})) {
              const agentConfig = config.config[agentKey];
              if (agentConfig?.systemPromptHash && !activeHashes.has(agentConfig.systemPromptHash)) {
                agentConfig.systemPromptHash = undefined;
                configsModified = true;
              }
            }
          }
          if (configsModified) {
            localStorage.setItem(CUSTOM_CONFIGS_KEY, JSON.stringify(allConfigs));
          }
        }

        // Re-read selected configs after potential pruning
        const validatedSelectedConfigs = allConfigs.filter((c: any) => ids.includes(c.id));
        setSelectedCustomConfigs(validatedSelectedConfigs);
      } catch (error) {
        console.error('Failed to load/validate selected custom configs on mount:', error);
      }
    };

    loadAndValidateConfigs();
  }, []);

  const handleScoreResults = async () => {
    if (!submittedPrompt || providerExecutions.length === 0) return;

    // Collect research results with answer text
    const researchResults = providerExecutions
      .map(pe => {
        const flow = executionFlows[pe.executionId];
        if (!flow?.finalResponse?.answer) return null;
        
        return {
          clientId: pe.executionId, // Use executionId as clientId for matching
          answerText: flow.finalResponse.answer
        };
      })
      .filter(r => r !== null);

    if (researchResults.length === 0) {
      alert('No research results available to score');
      return;
    }

    setIsScoring(true);
    try {
      const scores = await apiService.scoreResearchResults({
        userPrompt: submittedPrompt,
        researchResults
      });
      setScoringResults(scores);
    } catch (error) {
      console.error('Error scoring results:', error);
      alert('Failed to score results. Please try again.');
    } finally {
      setIsScoring(false);
    }
  };

  return (
    <AppLayout>
      {/* Content Area - Results */}
      <main data-component="MainPage" className="flex-1 overflow-hidden flex flex-col">
        {hasSubmitted ? (
          <>
            {/* Results Area */}
            <div className="flex-1 overflow-hidden flex flex-col relative">
              {/* Score Button - floating top-right, advanced mode only */}
              {!isSimplifiedView && providerExecutions.length > 1 && !isLoading && (
                <div className="absolute top-3 right-6 z-20">
                  <button
                    data-flow="score-button"
                    onClick={handleScoreResults}
                    disabled={isScoring}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium shadow-md"
                  >
                    <Award className="w-4 h-4" />
                    {isScoring ? 'Scoring...' : 'Score Results'}
                  </button>
                </div>
              )}

              {/* Scoring Results Banner */}
              {scoringResults && (
                <div data-flow="scoring-results" className="bg-blue-50 border-b border-blue-200 px-6 py-4 flex-shrink-0">
                  <div className="max-w-7xl mx-auto">
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-blue-900 mb-2">AI Scoring Results</h3>
                        {scoringResults.overallAnalysis && (
                          <p data-flow="overall-analysis" className="text-sm text-blue-800 mb-3">{scoringResults.overallAnalysis}</p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {scoringResults.scoredResults.map((result) => {
                            const providerExec = providerExecutions.find(pe => pe.executionId === result.clientId);
                            const accuracyPercent = Math.round(result.accuracy * 100);
                            return (
                              <div key={result.clientId} className="bg-white rounded-lg p-3 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-900 text-sm">
                                    {providerExec?.providerDisplayName || result.clientId}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 font-medium">Rank #{result.rank}</span>
                                    <span className={`px-2.5 py-1 rounded-lg text-sm font-bold border ${
                                      result.accuracy >= 0.90 ? 'bg-green-100 text-green-700 border-green-300' :
                                      result.accuracy >= 0.75 ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                      'bg-red-100 text-red-700 border-red-300'
                                    }`}>
                                      {accuracyPercent}%
                                    </span>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-600">{result.reasoning}</p>
                                {result.gapAnalysis && (
                                  <p data-flow="gap-analysis" className="text-xs text-amber-700 mt-1 italic">Gap: {result.gapAnalysis}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        onClick={() => setScoringResults(null)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-hidden flex">{providerExecutions.length === 1 ? (
                // Single provider - centered with max width
                <div data-flow="single-provider" className="flex-1 flex justify-center overflow-hidden">
                  <div className="w-full max-w-5xl flex">
                    {isSimplifiedView ? (
                      <SimplifiedSearchColumn
                        providerName={providerExecutions[0].providerDisplayName}
                        providerId={providerExecutions[0].providerId}
                        executionFlow={executionFlows[providerExecutions[0].executionId]}
                        isLoading={isLoading}
                        columnWidth="100%"
                        onRefresh={() => refreshProvider(providerExecutions[0].executionId, providerExecutions[0].providerId)}
                        executionEvents={executionEvents[providerExecutions[0].executionId] || []}
                        onSubmitClarification={signalRContext.submitClarification}
                        previousRounds={sessionHistory.map(round => {
                          const matchingExec = round.providerExecutions.find(pe => pe.providerDisplayName === providerExecutions[0].providerDisplayName);
                          return { prompt: round.prompt, executionFlow: matchingExec ? round.executionFlowsSnapshot[matchingExec.executionId] : undefined };
                        })}
                        currentPrompt={submittedPrompt}
                      />
                    ) : (
                      <ProviderResultColumn
                        providerName={providerExecutions[0].providerDisplayName}
                        providerId={providerExecutions[0].providerId}
                        executionFlow={executionFlows[providerExecutions[0].executionId]}
                        isLoading={isLoading}
                        columnWidth="100%"
                        onRefresh={() => refreshProvider(providerExecutions[0].executionId, providerExecutions[0].providerId)}
                        executionEvents={executionEvents[providerExecutions[0].executionId] || []}
                        onSubmitClarification={signalRContext.submitClarification}
                        previousRounds={sessionHistory.map(round => {
                          const matchingExec = round.providerExecutions.find(pe => pe.providerDisplayName === providerExecutions[0].providerDisplayName);
                          return { prompt: round.prompt, executionFlow: matchingExec ? round.executionFlowsSnapshot[matchingExec.executionId] : undefined };
                        })}
                        currentPrompt={submittedPrompt}
                      />
                    )}
                  </div>
                </div>
              ) : (
                // Multiple providers - grid layout
                <MultiProviderGrid
                  providerExecutions={providerExecutions}
                  executionFlows={executionFlows}
                  isLoading={isLoading}
                  onRefreshProvider={refreshProvider}
                  scoringResults={scoringResults ?? undefined}
                  isSimplifiedView={isSimplifiedView}
                  executionEvents={executionEvents}
                  onSubmitClarification={signalRContext.submitClarification}
                  sessionHistory={sessionHistory}
                  currentPrompt={submittedPrompt}
                />
              )}
            </div>
            </div>
          </>
        ) : (
          <div data-flow="welcome" className="px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center py-12">
                <Scale className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-medium text-gray-600 mb-2">
                  Welcome to AI Legal Research
                </h2>
                <p className="text-gray-500 max-w-2xl mx-auto mb-6">
                  Enter your legal research question below. Click the model icon to select 
                  multiple AI models and compare responses side-by-side.
                </p>
                <div className="mt-6 text-sm text-gray-400">
                  <p className="font-medium mb-2">Example questions:</p>
                  <ul className="space-y-1">
                    <li>"What are the key elements of contract formation?"</li>
                    <li>"Find cases about intellectual property infringement"</li>
                    <li>"What are the recent changes in employment law?"</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Section - Input Area */}
      <div className="bg-white border-t border-gray-200 flex-shrink-0 relative z-10">
        <div className="px-6 py-4">
          <div className="max-w-7xl mx-auto">
            {/* Query Input with favorite prompts at bottom */}
            <QueryInput
              prompt={prompt}
              onPromptChange={setPrompt}
              onSubmit={(mode) => {
                const customConfigs = selectedCustomConfigs.length > 0 ? selectedCustomConfigs : undefined;
                if (mode === 'continue') {
                  submitQuery(customConfigs, { continueSession: true });
                } else {
                  startNewSession();
                  submitQuery(customConfigs, { continueSession: false });
                }
              }}
              isLoading={isLoading}
              disabled={!connectionState.isConnected}
              cachedPrompts={cachedPrompts}
              onRemovePrompt={removeFromCache}
              onAddPrompt={addToCache}
              onOpenModelSelector={() => setIsModelSelectorOpen(!isModelSelectorOpen)}
              selectedModelsCount={selectedProviders.length + selectedCustomConfigs.length}
              customConfigName={selectedCustomConfigs.length === 1 ? selectedCustomConfigs[0].name : selectedCustomConfigs.length > 1 ? `${selectedCustomConfigs.length} custom configs` : ''}
              isModelSelectorOpen={isModelSelectorOpen}
              onSelectAll={() => selectAllRef.current?.()}
              usedSessionContext={usedSessionContext}
              sessionCanContinue={sessionCanContinue}
              sessionLimitReached={sessionLimitReached}
              onNewSession={startNewSession}
              modelSelectorContent={
                <ProviderSelector
                  selectedProviders={selectedProviders}
                  onSelectionChange={setSelectedProviders}
                  selectAllRef={selectAllRef}
                  onCustomConfigsChange={setSelectedCustomConfigs}
                  onMaxOpinionsChange={setProviderMaxOpinionsInHook}
                  onMaxContraryOpinionsChange={setProviderMaxContraryOpinionsInHook}
                  onMaxPassagesChange={setProviderMaxPassagesInHook}
                  onEmbeddingIndexesChange={setProviderEmbeddingIndexesInHook}
                  onUseReducerChange={setProviderReducerInHook}
                  onHitlChange={setProviderHitlInHook}
                  onFolioThresholdChange={setProviderFolioThresholdInHook}
                  onRelevanceCheckChange={setProviderRelevanceCheckInHook}
                  onMinSearchScoreChange={setProviderMinSearchScoreInHook}
                />
              }
            />
          </div>
        </div>
      </div>
      </AppLayout>
  );
}

/** Resolves route params and redirects to the new path prefix */
function LegacyRedirect({ to }: { to: string }) {
  const params = useParams();
  let resolved = to;
  for (const [key, value] of Object.entries(params)) {
    if (key !== '*' && value) {
      resolved = resolved.replace(`:${key}`, value);
    }
  }
  return <Navigate to={resolved} replace />;
}

function App() {
  const {
    isInitializing,
    healthStatus,
    error: initError,
    initializationMessage,
    authState,
  } = useHealthStatus();

  return (
    <BrowserRouter>
      <ViewModeProvider>
      <InitializationLoader
        isInitializing={isInitializing}
        healthStatus={healthStatus}
        error={initError}
        initializationMessage={initializationMessage}
        authState={authState}
      />
      <Routes>
        <Route path="/" element={<Navigate to="/opinions/search" replace />} />
        <Route path="/opinions/search" element={<MainPage />} />
        <Route path="/opinions/history" element={<ExecutionHistoryPage />} />
        <Route path="/opinions/history/:executionId" element={<ExecutionHistoryPage />} />
        <Route path="/opinions/evaluation" element={<EvaluationListView />} />
        <Route path="/opinions/evaluation/new" element={<CreateEvaluationTestPage />} />
        <Route path="/opinions/evaluation/:testId/edit" element={<EditEvaluationTestPage />} />
        <Route path="/opinions/evaluation/:testId/run" element={<EvaluationExecutionInterface />} />
        <Route path="/opinions/evaluation/batch-run" element={<BatchRunPage />} />
        <Route path="/opinions/evaluation/:testId/ai-retest" element={<AIRetestPage />} />
        <Route path="/opinions/evaluation/:testId/analytics" element={<EvaluationAnalyticsView />} />
        <Route path="/opinions/evaluation/:testId/compare/:sourceTestId" element={<AIComparisonView />} />
        <Route path="/opinions/scoring" element={<ScoringValidationListPage />} />
        <Route path="/opinions/scoring/new" element={<CreateScoringValidationPage />} />
        <Route path="/opinions/scoring/:runId" element={<ScoringValidationDashboard />} />
        <Route path="/opinions/prompts" element={<SystemPromptsPage />} />
        <Route path="/opinions/model-health" element={<ModelValidationPage />} />
        <Route path="/opinions/hallucination" element={<HallucinationTestsPage />} />
        <Route path="/civil-lawsuits/trends/queried" element={<TrendSearchPage />} />
        <Route path="/civil-lawsuits/trends/pre-computed" element={<PrecomputedTrendsPage />} />
        {/* Legacy redirects */}
        <Route path="/history" element={<Navigate to="/opinions/history" replace />} />
        <Route path="/evaluation" element={<Navigate to="/opinions/evaluation" replace />} />
        <Route path="/evaluation/new" element={<Navigate to="/opinions/evaluation/new" replace />} />
        <Route path="/evaluation/:testId/edit" element={<LegacyRedirect to="/opinions/evaluation/:testId/edit" />} />
        <Route path="/evaluation/:testId/run" element={<LegacyRedirect to="/opinions/evaluation/:testId/run" />} />
        <Route path="/evaluation/batch-run" element={<Navigate to="/opinions/evaluation/batch-run" replace />} />
        <Route path="/evaluation/:testId/ai-retest" element={<LegacyRedirect to="/opinions/evaluation/:testId/ai-retest" />} />
        <Route path="/evaluation/:testId/analytics" element={<LegacyRedirect to="/opinions/evaluation/:testId/analytics" />} />
        <Route path="/evaluation/:testId/compare/:sourceTestId" element={<LegacyRedirect to="/opinions/evaluation/:testId/compare/:sourceTestId" />} />
        <Route path="/scoring-validation" element={<Navigate to="/opinions/scoring" replace />} />
        <Route path="/scoring-validation/new" element={<Navigate to="/opinions/scoring/new" replace />} />
        <Route path="/scoring-validation/:runId" element={<LegacyRedirect to="/opinions/scoring/:runId" />} />
        <Route path="/prompts" element={<Navigate to="/opinions/prompts" replace />} />
        <Route path="/model-health" element={<Navigate to="/opinions/model-health" replace />} />
        <Route path="/hallucination-tests" element={<Navigate to="/opinions/hallucination" replace />} />
        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </ViewModeProvider>
    </BrowserRouter>
  );
}

function AppRoot() {
  useDebugMode();
  
  return (
    <HealthStatusProvider>
      <SignalRProvider>
        <App />
      </SignalRProvider>
    </HealthStatusProvider>
  );
}

export default AppRoot;
